'use client'

/**
 * HeaderContainer - Standard header wrapper for all pages except home
 *
 * WHY THIS EXISTS (vs HomeHeaderContainer):
 * Most pages need a solid white header from the start. Only the home page
 * needs the transparent-to-opaque behavior for its hero slideshow.
 *
 * This component handles:
 * - Standard solid white header background
 * - 'collapsed' state on mobile when user scrolls (shrinks header)
 *
 * CSS classes applied:
 * - 'header' (always) - standard header styles
 * - 'collapsed' - when scrolled on mobile
 *
 * See also: HomeHeaderContainer.js, (shop)/layout.js
 */

import { useState, useEffect } from 'react'

const HeaderContainer = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false)

    useEffect(() => {
        const handleScroll = () => {
            // Only apply collapsed behavior on mobile (< 1024px)
            const isDesktop = window.innerWidth >= 1024
            if (isDesktop) {
                if (collapsed) setCollapsed(false)
                return
            }

            const scrollY = window.scrollY
            // Add hysteresis to prevent flickering at threshold
            if (collapsed) {
                setCollapsed(scrollY > 20)
            } else {
                setCollapsed(scrollY > 100)
            }
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        window.addEventListener('resize', handleScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', handleScroll)
            window.removeEventListener('resize', handleScroll)
        }
    }, [collapsed])

    // Sync collapsed state to body class for components outside header
    useEffect(() => {
        if (collapsed) {
            document.body.classList.add('header-collapsed')
        } else {
            document.body.classList.remove('header-collapsed')
        }
    }, [collapsed])

    return (
        <header className={`group/header header ${collapsed ? 'collapsed' : ''}`}>
            {children}
        </header>
    )
}

export default HeaderContainer
